import { FieldPath, FieldValues, useController } from "react-hook-form";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  FormControl,
  InputLabel,
} from "@mui/material";
import React from "react";
import { RhfInputProps } from "../../../Forms/RhfInputProps";
import { useFetchEquipment } from "../Hooks/useFetchEquipment";
import { RHFFieldErrorText } from "../../../Forms/RHFFieldErrorText";
import { isArrayUndefinedOrEmpty } from "../../../Utils/arrays";
import { TextFieldReadOnly } from "../../../TransportNetwork/TTProtections/Form/AffectedSprs/MultiSelectMenues/TextFieldReadOnly";
import { textFieldDefaultProps } from "../../../Forms/TextFieldDefaultProps";
import { MuiSelectChip } from "../../../Layout/Select/MuiSelectChip";

type Props = {
  label?: string;
  disabled?: boolean;
};

export const RhfEquipmentMultiSelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  disabled,
}: RhfInputProps<TFieldValues, TName> & Props) => {
  const {
    field,
    fieldState: { error },
    formState: { disabled: isFormDisabled },
  } = useController({
    control,
    name,
  });
  const { data: equipments } = useFetchEquipment();
  const labels = equipments ? Object.fromEntries(equipments.map((e) => [e.Id, e.Name])) : {};

  return (
    <FormControl fullWidth size="small">
      {label && <InputLabel shrink>{label}</InputLabel>}
      <Autocomplete
        {...field}
        multiple
        noOptionsText="No options available"
        options={isArrayUndefinedOrEmpty(equipments) ? [] : equipments.map((e) => e.Id)}
        getOptionLabel={(optionId) => labels[optionId] ?? "NOT FOUND"}
        onChange={(event, values) => field.onChange(values)}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldReadOnly {...params} {...textFieldDefaultProps} label={label} />
        )}
        renderTags={(values, getTagProps) =>
          values.map((value, index) => (
            <MuiSelectChip
              getTagProps={getTagProps}
              labels={labels}
              index={index}
              valueId={value}
            />
          ))
        }
        size="small"
        filterSelectedOptions
        disableCloseOnSelect
        disabled={isFormDisabled || disabled}
      />
      {!!error && <RHFFieldErrorText fieldError={error} />}
    </FormControl>
  );
};
